/**
 * Project-level angle types for managing source angles (house exterior photos)
 * that belong to a specific project.
 */

import type { UploadPolicy } from '@/types/project-reference';

export type ProjectAngleValidationStatus =
  | 'pending'
  | 'processing'
  | 'valid'
  | 'invalid'
  | 'prohibited'
  | 'technical_error'
  // New terminal status (P5.1): photo is a different building than the
  // project's existing angles. Rides the standard invalid path.
  | 'different_building'
  // Brief-intake Phase 3: the uploaded image was a flat technical drawing /
  // 2D projection / document-like image. The server soft-deleted the angle and
  // AUTO-ROUTED it into the Brief documents (project_references section=brief).
  // A NEUTRAL/POSITIVE terminal outcome — the file moved, it did not fail.
  | 'info_source'
  // Brief-intake Phase 3: the image was NOT this building but inspiration
  // material (another house, a material/colour sample, a landscape/plant). The
  // server soft-deleted the angle and AUTO-ROUTED it into the References
  // (inspiration) section. Also a neutral/positive "moved, not failed" outcome.
  | 'reference'
  // The image was an artistic/illustrative rendering of a building (hand-drawn
  // sketch, watercolour, stylised illustration) valued for its visual STYLE, not
  // a render input. The server soft-deleted the angle and AUTO-ROUTED it into the
  // References section as an output-style reference (category=output). Mirrors
  // `reference` — a neutral/positive "moved, not failed" outcome.
  | 'output_style';

/**
 * Machine reason the API attaches when validation fails (P5.1). Kept as a loose
 * string so an unknown future reason never breaks the UI — components branch on
 * the known values they care about.
 */
export const DIFFERENT_BUILDING_REASON = 'different_building';

/**
 * Flat validation meta from the API. Mirrors `ConceptAngleResource` so a single
 * parser covers both endpoints. The `status` flag is the source of truth —
 * frontends must not derive "invalid" from a missing row or from combining
 * multiple fields (API-driven state flags rule).
 */
export interface ProjectAngleValidation {
  status: ProjectAngleValidationStatus;
  is_valid: boolean | null;
  can_replace: boolean;
  is_technical_error?: boolean;
  recommendations?: string | null;
  /** e.g. "different_building"; see DIFFERENT_BUILDING_REASON. */
  validation_failure_reason?: string | null;
  /**
   * Same-building override hook (P5.2): true only when a different-building
   * block can be lifted in place ("It's my building"). Pre-computed server-side
   * (AngleValidationMetaBuilder) so clients branch on one flag.
   */
  can_override_building?: boolean;
  /**
   * True when the classifier filed the FIRST upload of a building as
   * inspiration but the server accepted it as a view by default (status stays
   * 'valid', is_valid=true). `recommendations` then carries the server-localized
   * explanation with a hint to change the type if it was inspiration.
   */
  auto_accepted_as_view?: boolean;
  form_of_source?: string | null;
  source_building_style?: string | null;
  building_type?: string | null;
  stage_of_construction?: string | null;
  /**
   * Brief-intake Phase 3: false = a flat technical drawing / 2D projection /
   * CAD / document-like image (an info-source), not a render input. Defaults
   * to true. Render-candidate angles are the only ones the create-concept
   * picker offers.
   */
  is_render_candidate?: boolean;
  /**
   * View-suitability rank (higher = better source to redesign): real photo 100,
   * photoreal render 75, perspective sketch 50, flat facade projection 25,
   * not-a-view 0. Deterministic, computed server-side once at validation. Used
   * to auto-sort the source suggestions at concept creation (photos first, flat
   * facade projections last). Absent on rows validated before the field existed.
   */
  view_suitability?: number | null;
  /**
   * True when the server AUTO-ROUTED a drawing into the Brief documents
   * section (the angle is soft-deleted and a project_reference section=brief,
   * category=data was created). Pairs with `status: 'info_source'`.
   */
  routed_to_documents?: boolean;
  /**
   * True when the server AUTO-ROUTED an inspiration image into the References
   * section (the angle is soft-deleted and a project_reference section=reference,
   * aspect=style was created). Pairs with `status: 'reference'`.
   */
  routed_to_references?: boolean;
  /**
   * True when the server AUTO-ROUTED an artistic/illustrative rendering of a
   * building into the References section as an output-style reference (the angle
   * is soft-deleted and a project_reference section=reference, category=output
   * was created). Pairs with `status: 'output_style'`. The server ALSO sets
   * `routed_to_references` (output lives in the References section), so clients
   * that only branch on that flag still reconcile; this distinct flag lets them
   * show the precise "moved to output style" copy.
   */
  routed_to_output_style?: boolean;
  /**
   * The created project_reference id when the angle was routed — shared by
   * `routed_to_documents`, `routed_to_references` and `routed_to_output_style`
   * (one route per angle).
   */
  routed_reference_id?: string | null;
}

/**
 * How a view came to exist. `upload` is a photo the user took; `synthesized` is
 * a camera we CONSTRUCTED for a building that has never been photographed (its
 * geometry comes from the project's drawings and brief, not from an image).
 */
export type AngleSource = 'upload' | 'synthesized';

export interface ProjectAngleAttributes {
  file_id: string;
  name: string | null;
  description: string | null;
  original_filename: string | null;
  is_main: boolean;
  order: number;
  file_url: string | null;
  width: number | null;
  height: number | null;
  source?: AngleSource;
  /** Side the constructed camera looks at; null for uploads. */
  viewpoint?: string | null;
  /** Server-localized name of that side — the only displayable form. */
  viewpoint_label?: string | null;
  /**
   * False for a constructed view: there is no "before" to compare against, so
   * clients show no before slot, no fullscreen original and no replace action.
   * Absent on payloads that predate constructed views → treat as true.
   */
  has_source_image?: boolean;
  validation: ProjectAngleValidation;
  validation_performed_at: string | null;
  concepts_count: number;
  concept_numbers: Array<{ id: string; number: number }>;
  created_at: string;
  updated_at: string;
}

export interface ProjectAngle {
  id: string;
  type: 'angle';
  attributes: ProjectAngleAttributes;
}

export interface ProjectAngleUploadResponse {
  id: string;
  type: 'angle';
  attributes: {
    file: {
      md5: string;
      url: string | null;
    };
    // `upload_policy` is null for soft-deleted angles; callers must guard.
    upload_policy: UploadPolicy | null;
    original_filename: string | null;
    validation: ProjectAngleValidation;
  };
}

/**
 * Strategy the client picks when deleting a shared view that still has work in
 * other concepts. Mirrors the server enum `DeleteConflictStrategy`.
 */
export type DeleteConflictStrategy = 'delete_everywhere' | 'keep_rendered';

export interface AngleDeleteConflictConceptRef {
  concept_id: string;
  project_sequence_number: number;
  /** Present only on the has_renders section. */
  render_count?: number;
}

export interface AngleDeleteConflictSection {
  count: number;
  concepts: AngleDeleteConflictConceptRef[];
  /** Server-localized message — rendered verbatim (API-driven messages). */
  message: string;
}

export interface AngleDeleteConflictStrategyOption {
  key: DeleteConflictStrategy;
  /** Server-localized button label. */
  label: string;
  style: 'destructive' | 'default';
}

/**
 * `meta` payload of the 409 ANGLE_DELETE_CONFLICT response. Deleting a shared
 * view would touch other concepts; the client confirms a strategy and retries.
 */
export interface AngleDeleteConflictMeta {
  angle_id: string;
  origin_concept_id: string;
  conflicts: {
    has_renders: AngleDeleteConflictSection;
    is_main_elsewhere: AngleDeleteConflictSection;
  };
  available_strategies: AngleDeleteConflictStrategyOption[];
  /**
   * True when the server BLOCKS the delete outright (the view is the main view
   * of other designs — those must be deleted first). `available_strategies` is
   * empty in this case: there is no destructive option, only an informational
   * dialog. The component primarily branches on `available_strategies.length`
   * (robust even if this flag is absent), this is the explicit server signal.
   */
  is_blocked?: boolean;
}

export function getProjectAngleValidationStatus(
  angle: ProjectAngle
): ProjectAngleValidationStatus {
  return angle.attributes.validation.status;
}

export function isProjectAngleValid(angle: ProjectAngle): boolean {
  return angle.attributes.validation.status === 'valid';
}

/**
 * True unless the server flagged the image as an info-source (drawing/document)
 * that is not a render input. Info-source angles are soft-deleted server-side
 * (so they never reach the pickers), but this guard is belt-and-suspenders for
 * any list that still carries one: `is_render_candidate === false` is excluded.
 */
export function isRenderCandidate(angle: ProjectAngle): boolean {
  return angle.attributes.validation.is_render_candidate !== false;
}

/**
 * View-suitability rank of an angle (higher = better source to redesign).
 * Falls back to 0 when the server hasn't stamped the field (old rows) so such
 * angles sort after ranked ones without throwing off the comparator.
 */
export function getViewSuitability(angle: ProjectAngle): number {
  return angle.attributes.validation.view_suitability ?? 0;
}

/**
 * Order source suggestions for concept creation: best view source first
 * (photo > render > sketch > flat projection), tie-broken by the manual
 * `order` position so equally-good sources keep their library order. Stable and
 * shared so Web mirrors Apple/Android.
 */
export function compareByViewSuitability(
  a: ProjectAngle,
  b: ProjectAngle
): number {
  return (
    getViewSuitability(b) - getViewSuitability(a) ||
    a.attributes.order - b.attributes.order
  );
}

export function getProjectAngleDisplayName(
  angle: ProjectAngle,
  index: number
): string {
  return (
    angle.attributes.name ||
    angle.attributes.original_filename ||
    `Angle #${index + 1}`
  );
}
